import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  Progress,
  SearchBar,
  TableActionTypes,
  useDataTable,
} from 'web-library';
import { usePostAppealRegistrationCheckEligibility } from '../../../../api-refactored/appeal/usePostAppealRegistrationCheckEligibility';
import {
  Candidate,
  usePostCandidateSearchNoPagination,
} from '../../../../api-refactored/user/usePostCandidateSearchNoPagination';
import FormHeader from '../../../../components/form-items/FormHeader';
import { useUniqueLink } from '../../../../pages/uniqueLink/AdminUniqueLink/logic/useUniqueLink';
import OpsAdminGrantAppealAccessModal from './OpsAdminGrantAppealAccessModal';

const useStyles = makeStyles()((theme) =>
({
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  viewDetailsButton: {
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    whiteSpace: 'nowrap',
    height: theme.spacing(3),
  },
  loadingIndicatorContainer: {
    marginLeft: theme.spacing(5),
  },
}));

/** define Mui DataTable Column Definition
 * */

interface TableColumnsProps {
  classes: any;
  t: TFunction;
  candidateList: Candidate[];
  handleGrantAccess: (name: string, uuid: string, index: number) => void;
  sendEmailStatusList: string[];
  checkEligibilityLoading: boolean;
  currentRowIndex: number;
  isEnableCreate: boolean;
}

const tableColumns = ({
  classes,
  t,
  candidateList,
  handleGrantAccess,
  sendEmailStatusList,
  checkEligibilityLoading,
  currentRowIndex,
  isEnableCreate,
}: TableColumnsProps): MUIDataTableColumnDef[] => [
    {
      name: 'uinFinPassport',
      label: t('admin->>UniqueLink.UinFinOrPassword'),
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'candidateName',
      label: t('admin->>UniqueLink.CandidateName'),
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'email',
      label: t('admin->>UniqueLink.Email'),
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'school',
      label: t('admin->>UniqueLink.School'),
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'action',
      label: t('admin->>UniqueLink.Action'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          let component;
          if (!sendEmailStatusList[dataIndex] || sendEmailStatusList[dataIndex] !== 'success') {
            if (currentRowIndex === dataIndex && checkEligibilityLoading) {
              component = (
                <div className={classes.loadingIndicatorContainer}>
                  <Progress color="secondary" size="M" />
                </div>
              );
            } else {
              component = (
                <Button
                  className={classes.viewDetailsButton}
                  color="primary"
                  variant="text"
                  size="small"
                  onClick={() => {
                    handleGrantAccess(
                      candidateList[dataIndex].candidateName,
                      candidateList[dataIndex].uuid,
                      dataIndex
                    );
                  }}
                >
                  {t('admin->>UniqueLink.GrantAccess')}
                </Button>
              );
            }
          } else {
            component = (
              <Pill label={t('admin->>UniqueLink.EmailSent')} size="S" variant="positive" />
            );
          }
          return component;
        },
        sort: false,
        filter: false,
        display: `${isEnableCreate}`,
      },
    },
  ];

interface CandidateAppealAccessListProps {
  isEnableCreate: boolean;
}

const CandidateAppealAccessList = ({ isEnableCreate }: CandidateAppealAccessListProps) => {
  const { t } = useTranslation();
  const [helperText, setHelperText] = useState<string>();
  const [searchError, setSearchError] = useState<boolean>(false);
  const [currentCandidate, setCurrentCandidate] = useState<{
    name: string;
    uuid: string;
    index: number;
  }>({ name: '', uuid: '', index: 0 });

  const {
    grantAccessModalOpen,
    selectedUserInfo,
    sendEmailStatusList,
    setSentEmailStatusList,
    apiEmailStatusCallback,
    onGrantAccessDialogClose,
    setEditDetail,
    eligibilityStatusCallback,
  } = useUniqueLink();

  const { classes } = useStyles();
  const { state, dispatch, tOptions } = useDataTable();

  const { mutate, isLoading } = usePostCandidateSearchNoPagination({
    onSuccess: (data) => {
      if (data) {
        dispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.content,
            count: data.totalElements,
          },
        } as TableActionTypes);
      }
    },
  });

  const {
    mutate: checkEligibilityMutate,
    isLoading: checkEligibilityLoading,
  } = usePostAppealRegistrationCheckEligibility({
    onSuccess: (data) => {
      setEditDetail(currentCandidate.name, currentCandidate.uuid, currentCandidate.index);
    },
  });

  const handleGrantAccess = (name: string, uuid: string, index: number) => {
    setCurrentCandidate({ name, uuid, index });
  };

  const resetDataTable = () => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: [],
        count: 0,
      },
    } as TableActionTypes);

    dispatch({
      type: 'UPDATE_SEARCH',
      payload: '',
    });
  };

  /** handle candidate search
   * */
  const onCandidateSearch = (candidateFilter: string) => {
    dispatch({
      type: 'UPDATE_SEARCH',
      payload: candidateFilter.trim(),
    });
    setHelperText(undefined);
    setSearchError(false);
    setSentEmailStatusList([]);
    mutate({ q: candidateFilter, apiParams: { pageSize: state.size, page: state.page } });
  };

  useEffect(() => {
    if (state.search) {
      mutate({ q: state.search, apiParams: { pageSize: state.size, page: state.page } });
    }
  }, [state.search, state.order, state.sortColumn, state.page, state.size, mutate]);

  useEffect(() => {
    if (currentCandidate.uuid) {
      checkEligibilityMutate({
        uuid: currentCandidate.uuid,
        userType: 'CANDIDATES',
      });
    }
  }, [checkEligibilityMutate, currentCandidate]);

  return (
    <>
      <Grid container direction="column">
        <FormHeader title={t('admin->>UniqueLink.CandidateDetails')} />

        <Box>
          <SearchBar
            name="searchInput"
            ariaLabel="searchInput"
            defaultValue=""
            placeholder={t('admin->>UniqueLink.SearchNameUinFinOrPassword')}
            helperText={helperText}
            onSearch={(str) => {
              if (str === '') {
                setHelperText(t('admin->>UniqueLink.SearchErrorNameUinFinOrPassword'));
                setSearchError(true);
                resetDataTable();
                return;
              }
              if (str.length < 3) {
                setHelperText(
                  t('admin->>UniqueLink.SearchErrorMinCharacters', { minSearchLength: 3 })
                );
                setSearchError(true);
                resetDataTable();
                return;
              }
              onCandidateSearch(str);
            }}
            error={searchError}
          />
        </Box>
        {grantAccessModalOpen && selectedUserInfo && (
          <OpsAdminGrantAppealAccessModal
            accountType="CANDIDATES"
            userInfo={selectedUserInfo}
            modalState={grantAccessModalOpen}
            onDialogClose={onGrantAccessDialogClose}
            updateEmailStatus={apiEmailStatusCallback}
          />
        )}
      </Grid>
      <div className={classes.tableContainer}>
        <MUIDataTable
          key="candidate"
          isLoading={isLoading}
          columns={tableColumns({
            classes,
            t,
            candidateList: state.data,
            handleGrantAccess,
            sendEmailStatusList,
            checkEligibilityLoading,
            currentRowIndex: currentCandidate.index,
            isEnableCreate,
          })}
          data={state.data}
          dispatch={dispatch}
          state={state}
          options={{
            ...tOptions,
            filter: false,
            sort: false,
            pagination: true,
          }}
          rowsPerPageOptions={[5, 10, 20, 25]}
        />
      </div>
    </>
  );
};

export default CandidateAppealAccessList;
